import React, { useEffect, useState } from "react";
import { Menu, Checkbox, Row } from "antd";
import "./index.css";
import {
  useGetEnquiryCategoryQuery,
  useGetExecutivesFilterQuery,
  useGetSalesProductDropdownItemsQuery,
  useGetQuotationProductsDropdownQuery,
} from "../../services/api";
import { CommonButton } from "../CommonButton/button";
import { CheckboxValueType } from "antd/es/checkbox/Group";

const { SubMenu } = Menu;

interface IFilter {
  setFilter: (data: boolean) => void;
  setExecutiveIds: (data: number[]) => void;
  filter: boolean;
  selectedExecutiveIds: number[];
  setEnquiryCategoryIds?: (data: number[]) => void;
  setStatusFilter?: (data: string[]) => void;
  setSelectedExecutiveIds: (data: number[]) => void;
  setProductIds: (data: number[]) => void;
  selectedProductIds: number[];
  setSelectedProductIds: (data: number[]) => void;
  selectedEnquiryCategoryIds?: number[];
  setSelectedEnquiryCategoryIds?: (data: number[]) => void;
  selectedStatusFilter?: string[];
  setSelectedStatusFilter?: (data: string[]) => void;
  activeTab?: string;
}

interface ProductItem {
  id: number;
  name: string;
}

// Status filter options - CORRECTED mapping
// When user selects "Active" → send is_closed = false
// When user selects "Closed" → send is_closed = true
const statusOptions = [
  { id: "false", name: "Active" },   // false means Active (not closed)
  { id: "true", name: "Closed" },    // true means Closed
];

export const ReportsFilterMenu: React.FC<IFilter> = ({
  setFilter,
  setExecutiveIds,
  setProductIds,
  setEnquiryCategoryIds,
  setStatusFilter,
  filter,
  selectedExecutiveIds,
  setSelectedExecutiveIds,
  selectedProductIds,
  setSelectedProductIds,
  selectedEnquiryCategoryIds = [],
  setSelectedEnquiryCategoryIds,
  selectedStatusFilter = [],
  setSelectedStatusFilter,
  activeTab = "orders",
}) => {
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const isManager = localStorage.getItem("user_role");

  // filter params for executives based on selected products
  const executiveFilterParams = {
    ...(selectedProductIds.length > 0 && {
      finished_goods_id: selectedProductIds.join(","),
    }),
  };

  // filter params for products based on selected executives
  const productFilterParams = {
    ...(selectedExecutiveIds.length > 0 && {
      executive_id: selectedExecutiveIds.join(","),
    }),
  };

  const { data: executivesData, refetch: refetchExecutives } =
    useGetExecutivesFilterQuery(executiveFilterParams);
  const { data: productsData, refetch: refetchProducts } =
    useGetSalesProductDropdownItemsQuery(productFilterParams);

  const { data: enquiryCategoryData } = useGetEnquiryCategoryQuery({});

  const { data: quotationProductsData } = useGetQuotationProductsDropdownQuery({});

  // Refetch when dependent filters change
  useEffect(() => {
    refetchExecutives();
  }, [selectedProductIds]);

  useEffect(() => {
    refetchProducts();
  }, [selectedExecutiveIds]);

  const handleExecutiveChange = (checkedValues: any[]) => {
    setSelectedExecutiveIds(checkedValues);
  };

  const handleProductChange = (checkedValues: any[]) => {
    setSelectedProductIds(checkedValues);
  };

  const handleEnquiryCategoryChange = (checkedValues: CheckboxValueType[]) => {
    setSelectedEnquiryCategoryIds?.(checkedValues as number[]);
  };

  const handleStatusChange = (checkedValues: CheckboxValueType[]) => {
    setSelectedStatusFilter?.(checkedValues as string[]);
  };

  const handleFilter = () => {
    setExecutiveIds(selectedExecutiveIds);
    setProductIds(selectedProductIds);
    if (setEnquiryCategoryIds) {
      setEnquiryCategoryIds(selectedEnquiryCategoryIds);
    }
    if (setStatusFilter) {
      setStatusFilter(selectedStatusFilter);
    }
    setFilter(false);
  };

  const handleCancel = () => {
    setSelectedExecutiveIds([]);
    setSelectedProductIds([]);
    setSelectedEnquiryCategoryIds?.([]);
    setSelectedStatusFilter?.([]);
    setExecutiveIds([]);
    setProductIds([]);
    if (setEnquiryCategoryIds) {
      setEnquiryCategoryIds([]);
    }
    if (setStatusFilter) {
      setStatusFilter([]);
    }
    setFilter(false);
  };

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  return (
    <Menu
      mode="inline"
      style={{ width: 215 }}
      defaultOpenKeys={openKeys}
      openKeys={openKeys}
      onOpenChange={handleOpenChange}
      className="filter-drop-down"
    >
      {/* Render executives filter */}
      {isManager === "1" && (
      <SubMenu key="executives" title="Executives" className="w-full">
        <Checkbox.Group
          value={selectedExecutiveIds}
          onChange={handleExecutiveChange}
        >
          {executivesData?.results?.map((item: any) => (
            <Menu.Item key={item.id}>
              <Checkbox value={item.id}>{item.full_name}</Checkbox>
            </Menu.Item>
          ))}
        </Checkbox.Group>
      </SubMenu>
      )}

      {/* Product Filter */}
      <SubMenu key="products" title="Products" className="w-full">
        <div className="filter-checkbox-group">
          <Checkbox.Group
            value={selectedProductIds}
            onChange={handleProductChange}
            className="groupData"
          >
            {(activeTab === "quotations" ? quotationProductsData?.data : productsData?.data)?.map((item: ProductItem) => (
              <Checkbox
                key={item.id}
                value={item.id}
                style={{ marginBottom: "8px" }}
              >
                {item.name}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
      </SubMenu>

      {/* Enquiry Category Filter - Only for Quotations tab */}
      {activeTab === "quotations" && (
        <SubMenu
          key="enquiryCategory"
          title="Enquiry Category"
          className="w-full"
        >
          <div className="filter-checkbox-group">
            <Checkbox.Group
              value={selectedEnquiryCategoryIds}
              onChange={handleEnquiryCategoryChange}
            >
              {enquiryCategoryData?.map((item: any) => (
                <Checkbox key={item.id} value={item.id}>
                  {item.name}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </SubMenu>
      )}

      {/* Status Filter - Only for Quotations tab */}
      {activeTab === "quotations" && (
        <SubMenu key="status" title="Status" className="w-full">
          <div className="filter-checkbox-group">
            <Checkbox.Group
              value={selectedStatusFilter}
              onChange={handleStatusChange}
              className="status-checkbox-group"
            >
              {statusOptions.map((item) => (
                <Checkbox key={item.id} value={item.id}>
                  {item.name}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </SubMenu>
      )}

      {/* Render filter and cancel buttons*/}
      <Menu.Item
        key="filter"
        style={{
          textAlign: "start",
        }}
        className="filter-drop-down"
      >
        <Row justify={"space-between"}>
          <CommonButton
            variant="wearlay-without-bg"
            onClick={handleCancel}
            value="Reset"
            className="reset"
          />
          <CommonButton
            variant="wealay-primary-btn"
            onClick={handleFilter}
            value="Ok"
          />
        </Row>
      </Menu.Item>
    </Menu>
  );
};

export default ReportsFilterMenu;